import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from '../program-sheet.module.scss';

import { DialogItem } from '@smart-tv/components/dialog';
import RecordingIndicator from '@smart-tv/components/icons/recording-indicator';
import ImageFilled from '@smart-tv/components/image-filled/image-filled';
import Typography from '@smart-tv/components/typography/typography';
import testIds from '@smart-tv/e2e/shared/variables';

import { ProviderPickerVersionType } from '../_stores/store-provider-version';
import { rootStore } from '../_stores/store-root';
import { uiStore } from '../_stores/store-ui';
import { useStreamActions } from '../_stores/useStreamActions';
import { renderAirtime } from './sheet-description-info-airtime';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';

const SheetProviders = observer(() => {
  const { handlePlay, handleStartOver } = useStreamActions();
  const { focusedEpisode } = rootStore.episodesStore;
  const { player } = usePlayerContext();

  const handleSelectProviderVersion = React.useCallback(
    (selecteVersionIndex: number) => {
      if (uiStore.toggles.invokeStartOver) {
        handleStartOver(selecteVersionIndex);
        uiStore.toggleInvokeStartOver(false);
      } else {
        handlePlay(selecteVersionIndex);
      }
    },
    [handlePlay, handleStartOver]
  );

  const handleCloseProvidersVersionsDialog = React.useCallback(() => {
    uiStore.toggleProvidersVersionsDialog(false);
    uiStore.toggleInvokeStartOver(false);
  }, []);

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event)) {
        event.stopImmediatePropagation();
        handleCloseProvidersVersionsDialog();
      }
    },
    [handleCloseProvidersVersionsDialog]
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  React.useEffect(() => {
    if (player) {
      uiStore.toggleProvidersVersionsDialog(false);
    }
  }, [player]);

  React.useEffect(() => {
    return () => {
      uiStore.toggleProvidersVersionsDialog(false);
    };
  }, []);

  return (
    <div className={styles['sheet-providers']}>
      <div
        className={styles['sheet-providers__content']}
        data-testid={testIds.programsheet.providers.picker}
      >
        <Typography variant="h3" data-testid={testIds.programsheet.providers.title}>
          Vælg udgave
        </Typography>
        {focusedEpisode.episodeTitle && (
          <Typography variant="h5">{focusedEpisode.episodeTitle}</Typography>
        )}
        <div className={styles['sheet-providers__items']}>
          {rootStore.providerVersionStore.providersVersionsPickerModel?.map(
            (version: ProviderPickerVersionType, index: number) => (
              <div
                className={styles['sheet-providers__item']}
                key={version.eventId || version.mediaId}
                data-testid={testIds.programsheet.providers.item}
              >
                <DialogItem
                  isFocused={index === 0}
                  selected={false}
                  onEnterPress={() => handleSelectProviderVersion(index)}
                >
                  {Boolean(version.eventId || version.mediaId) && (
                    <div className={styles['sheet-providers__item-content']}>
                      <ImageFilled
                        className={styles['sheet-providers__item-image']}
                        width={98}
                        height={64}
                        src={version.providerLogo ?? ''}
                        alt={version.providerName}
                        borderRadius={2}
                      />
                      <div className={styles['sheet-providers__item-details']}>
                        <Typography variant="body" fontWeight="bold">
                          {version.providerName}
                        </Typography>
                        {version.npvrInfo ? (
                          <Typography variant="caption2" gutterTop={2}>
                            <RecordingIndicator
                              className={styles['sheet-providers__item-npvr-icon']}
                              size={'lg'}
                            />
                            <div className={styles['sheet-providers__item-npvr-text']}>
                              {renderAirtime(
                                new Date(Number(version.npvrInfo.recordedAt) * 1000),
                                undefined,
                                true
                              )}
                            </div>
                          </Typography>
                        ) : (
                          <Typography variant="caption2">Kvalitet: {version.resolution}</Typography>
                        )}
                      </div>
                    </div>
                  )}
                </DialogItem>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
});

SheetProviders.displayName = 'SheetProviders';

export default SheetProviders;
